/** Midnight on the local calendar day of `date`. */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export type MonitoringStartPreset = '30d' | '14d' | 'today';
export type MonitoringDurationPreset = '1m' | '3m' | '6m' | '1y';

const MONITORING_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

const DAY_MS = 86400000;

/** Calendar day `days` before `today`. */
export function daysBefore(today: Date, days: number): Date {
  const date = startOfLocalDay(today);
  date.setDate(date.getDate() - days);
  return date;
}

/** Calendar day `months` after `date`. */
export function addMonths(date: Date, months: number): Date {
  const next = startOfLocalDay(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

/** Earliest start: 30 days before today. */
export function earliestMonitoringStart(today: Date): Date {
  return daysBefore(today, 30);
}

/** Latest end: one year after the chosen start. */
export function latestMonitoringEnd(start: Date): Date {
  return addMonths(start, 12);
}

/** The start day for a preset, measured back from today. */
export function monitoringStart(preset: MonitoringStartPreset, today: Date): Date {
  if (preset === '30d') {
    return daysBefore(today, 30);
  }
  if (preset === '14d') {
    return daysBefore(today, 14);
  }
  return startOfLocalDay(today);
}

/** The end day for a duration, measured forward from the start. */
export function monitoringEnd(start: Date, preset: MonitoringDurationPreset): Date {
  const months = preset === '1m' ? 1 : preset === '3m' ? 3 : preset === '6m' ? 6 : 12;
  return addMonths(start, months);
}

/** Whole days from `from` until `to`. */
export function daysBetween(from: Date, to: Date): number {
  const ms = startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime();
  return Math.round(ms / DAY_MS);
}

export function formatMonitoringDay(date: Date): string {
  return `${date.getDate()} ${MONITORING_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function fromIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** True when `date` is today or up to 30 days before today. */
export function isValidMonitoringStart(date: Date, today: Date): boolean {
  const day = startOfLocalDay(date).getTime();
  return day >= earliestMonitoringStart(today).getTime() && day <= startOfLocalDay(today).getTime();
}

/** True when `end` is after `start` and no later than one year after `start`. */
export function isValidMonitoringEnd(start: Date, end: Date): boolean {
  const endDay = startOfLocalDay(end).getTime();
  return endDay > startOfLocalDay(start).getTime() && endDay <= latestMonitoringEnd(start).getTime();
}

export function matchingStartPreset(start: Date, today: Date): MonitoringStartPreset | null {
  const day = startOfLocalDay(start).getTime();
  const presets: MonitoringStartPreset[] = ['30d', '14d', 'today'];
  return presets.find((preset) => monitoringStart(preset, today).getTime() === day) ?? null;
}

export function matchingDurationPreset(start: Date, end: Date): MonitoringDurationPreset | null {
  const day = startOfLocalDay(end).getTime();
  const presets: MonitoringDurationPreset[] = ['1m', '3m', '6m', '1y'];
  return presets.find((preset) => monitoringEnd(start, preset).getTime() === day) ?? null;
}

export type MonitoringTimeline = {
  shade: { left: number; width: number };
  range: { left: number; width: number };
  todayLeft: number;
};

/** Track from 30 days before today to one year after today. */
export function monitoringTimeline(start: Date | null, end: Date | null, today: Date): MonitoringTimeline {
  const trackStart = earliestMonitoringStart(today);
  const trackEnd = addMonths(today, 12);
  const span = trackEnd.getTime() - trackStart.getTime();
  const percent = (date: Date): number => {
    if (span <= 0) {
      return 0;
    }
    const clamped = Math.min(trackEnd.getTime(), Math.max(trackStart.getTime(), startOfLocalDay(date).getTime()));
    return Math.round(((clamped - trackStart.getTime()) / span) * 100);
  };
  const todayLeft = percent(today);
  const shade = { left: 0, width: todayLeft };
  if (!start || !end) {
    return { shade, range: { left: todayLeft, width: 0 }, todayLeft };
  }
  const left = percent(start);
  const right = percent(end);
  return { shade, range: { left, width: Math.max(0, right - left) }, todayLeft };
}
